<template>
  <div class="grid-container mt-6 md:mt-14">
    <Head title="Editar perfil" />
    <div class="flex justify-between items-center">
      <header>
        <h1 class="headline-md text-primary font-bold md:display-sm">
          Editar perfil de empresa
        </h1>
      </header>
      <div class="hidden md:inline-block">
        <Button
          type="submit"
          form="company-profile-form"
          icon="round-save"
          size="large"
          class="btn-filled h-14"
          :isLoading="form.processing"
          :disabled="!isFormChanged"
        >
          Guardar cambios
        </Button>
      </div>
    </div>
    <form id="company-profile-form" class="my-4 flex flex-col gap-4" @submit.prevent="submitForm">
      <div class="md:p-6 md:border md:border-outline/16 md:rounded-2xl md:surface1">
        <h2 class="title-lg text-primary30 font-medium my-3">Información básica</h2>
        <div class="md:grid md:grid-cols-3 md:gap-6">
          <div>
            <TextInput
              id="company_name"
              v-model="form.name"
              label="Nombre de la Empresa"
              :error="errors.name"
              required
            />
            <FormTextarea
              id="description"
              v-model="form.description"
              label="Descripción"
              hint="Puedes incluir una breve descripción de las actividades que realizaste en tu institución"
              required
              :maxlength="300"
              :error="errors.description"
            />
          </div>
          <div>
            <FormSelect
              id="company_size"
              v-model="form.company_size_id"
              label="Tamaño"
              required
              :error="errors.company_size_id"
            >
              <option value="" disabled>Seleccionar</option>
              <option v-for="size in companySizes" :key="size.id" :value="size.id">
                {{ size.name }} ({{ size.min_employee_count }} -
                {{ size.max_employee_count }} empleados)
              </option>
            </FormSelect>
            <FormSelect
              id="company_category"
              v-model="form.company_category_id"
              label="Rubro"
              required
              :error="errors.company_category_id"
            >
              <option value="" disabled>Seleccionar</option>
              <option v-for="category in companyCategories" :key="category.id" :value="category.id">
                {{ category.name }}
              </option>
            </FormSelect>
          </div>
          <div>
            <h3 class="title-md text-primary20 font-medium my-3 md:hidden">Redes sociales</h3>
            <TextInput
              id="fb_url"
              v-model="form.fb_url"
              label="Facebook"
              placeholder="https://"
              icon="round-facebook"
              :error="errors.fb_url"
            />
            <TextInput
              id="linkedin_url"
              v-model="form.linkedin_url"
              label="LinkedIn"
              placeholder="https://"
              icon="linkedin"
              :error="errors.linkedin_url"
            />
            <TextInput
              id="website_url"
              v-model="form.website_url"
              label="Sitio web"
              placeholder="https://"
              icon="web"
              :error="errors.website_url"
            />
          </div>
        </div>
      </div>
      <div class="md:p-6 md:border md:border-outline/16 md:rounded-2xl md:surface1">
        <h2 class="title-lg text-primary30 font-medium my-3">Misión, visión y valores</h2>
        <div class="md:grid md:grid-cols-3 md:gap-6">
          <FormTextarea
            id="company_mission"
            v-model="form.company_mission"
            label="Misión"
            :maxlength="300"
            :rows="5"
            :error="errors.company_mission"
          />
          <FormTextarea
            id="company_vision"
            v-model="form.company_vision"
            label="Visión"
            :maxlength="300"
            :rows="5"
            :error="errors.company_vision"
          />
          <FormTextarea
            id="company_values"
            v-model="form.company_values"
            label="Valores"
            :maxlength="300"
            :rows="5"
            :error="errors.company_values"
          />
        </div>
      </div>
      <div class="md:grid md:grid-cols-2 md:gap-4">
        <div>
          <div class="md:p-6 md:border md:border-outline/16 md:rounded-2xl md:surface1">
            <div class="flex flex-wrap justify-between items-center">
              <h2 class="title-lg text-primary30 font-medium my-3">Galería</h2>
              <Button
                v-if="false"
                type="button"
                icon="add"
                class="btn-filled brand-green"
                @click="$modal.show('modal_companyMultimediaAddForm')"
                >Agregar multimedia</Button
              >
            </div>
            <div class="flex flex-col gap-4 mt-4">
              <div
                v-for="(item, key) in multimedia_form"
                :key="key"
                class="flex flex-col xs:flex-row gap-2 bg-surface border border-outline/16 rounded-2xl"
              >
                <img
                  :src="item.type === 'youtube' ? getYoutubeThumbnail(item.url).hq : item.url"
                  class="h-auto rounded-lg rounded-r-none object-cover w-full xs:w-36"
                />
                <div class="flex flex-col gap-2 p-2 w-full">
                  <div class="flex gap-2">
                    <span class="py-1 px-3 border border-outline/30 rounded-lg label-md">
                      #{{ key + 1 }}
                    </span>
                    <span class="py-1 px-3 border border-outline/30 rounded-lg label-md">
                      {{ item.type == 'youtube' ? 'YouTube' : 'Imagen' }}
                    </span>
                    <span
                      v-if="item._action == 'create'"
                      class="py-1 px-3 border border-outline/30 bg-tertiary-container text-on-tertiary-container rounded-lg label-md"
                    >
                      Nuevo
                    </span>
                  </div>
                  <p
                    v-if="item._action == 'create' && item.type == 'image'"
                    class="label-lg font-medium underline underline-offset-4 decoration-2 decoration-brand-bright-green"
                  >
                    Nueva imagen
                  </p>
                  <a
                    v-else
                    class="label-lg font-medium underline underline-offset-4 decoration-2 decoration-brand-bright-green"
                    :href="item.url"
                    target="_blank"
                    >{{ item.url }}</a
                  >
                  <div class="flex flex-wrap gap-2 mt-4 justify-center xs:justify-end">
                    <Button
                      type="button"
                      class="btn-tonal"
                      :disabled="key <= 0"
                      @click="updateMultimediaOrder(key, -1)"
                      >Subir</Button
                    >
                    <Button
                      type="button"
                      class="btn-tonal"
                      :disabled="key == multimedia_form.length - 1"
                      @click="updateMultimediaOrder(key, 1)"
                      >Bajar</Button
                    >
                    <Button type="button" class="btn-tonal btn-error" @click="deleteMultimedia(key)"
                      >Eliminar</Button
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-6 md:mt-0">
          <div class="md:p-6 md:border md:border-outline/16 md:rounded-2xl md:surface1">
            <h2 class="title-lg text-primary30 font-medium my-3">Foto de perfil y banner</h2>
            <h3 class="title-md text-primary20 font-medium my-3">Foto de Perfil</h3>
            <FormInputFile
              accept="image/jpg,image/jpeg,image/png"
              show-file-size
              :max-file-size="2097152"
              @update-files="
                form.profile_image = Array.isArray($event) && $event.length > 0 ? $event[0] : null
              "
            />
            <h3 class="title-md text-primary20 font-medium my-3">Banner de Perfil</h3>
            <FormInputFile
              accept="image/jpg,image/jpeg,image/png"
              show-file-size
              :max-file-size="2097152"
              @update-files="
                form.banner_image = Array.isArray($event) && $event.length > 0 ? $event[0] : null
              "
            />
          </div>
        </div>
      </div>
    </form>
    <Button
      type="submit"
      form="company-profile-form"
      icon="round-save"
      size="large"
      class="btn-filled h-14 mx-auto mt-10"
      :isLoading="form.processing"
      :disabled="!isFormChanged"
    >
      Guardar cambios
    </Button>
    <modal
      name="modal_companyMultimediaAddForm"
      height="auto"
      :clickToClose="false"
      reset
      adaptive
      scrollable
      classes="!surface-1 md:!rounded-xl !overflow-y-auto"
      @close="$modal.hide('modal_companyMultimediaAddForm')"
    >
      <CompanyMultimediaAddForm
        @add-multimedia="addMultimedia"
        @close="$modal.hide('modal_companyMultimediaAddForm')"
      />
    </modal>
  </div>
</template>

<script>
import { cleanNullValuesFromObject, getYoutubeThumbnail } from '@/utilities.helpers.js'
import { Head } from '@inertiajs/inertia-vue'
import Layout from '@/Layouts/Layout'

export default {
  name: 'CompanyProfileEdit',
  layout: Layout,
  components: {
    Head,
    Button: () => import('@/Components/Button'),
    TextInput: () => import('@/Components/FormInputText'),
    FormTextarea: () => import('@/Components/FormTextarea'),
    FormSelect: () => import('@/Components/FormSelect'),
    CheckInput: () => import('@/Components/FormInputCheck'),
    FormInputFile: () => import('@/Components/FormInputFile'),
    ChipListItem: () => import('@/Components/ChipListItem'),
    CompanyMultimediaAddForm: () => import('@/Components/CompanyMultimediaAddForm'),
  },
  props: {
    previousData: {
      type: Object,
      required: true,
    },
    companySizes: {
      type: Array,
      required: true,
    },
    companyCategories: {
      type: Array,
      required: true,
    },
    errors: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      multimedia_form: this.previousData.multimedia,
      form: this.$inertia.form({
        ...this.previousData,
        profile_image: null,
        banner_image: null,
        _method: 'put',
      }),
    }
  },
  computed: {
    isFormChanged() {
      return this.form.isDirty || this.multimedia_form != this.previousData.multimedia
    },
  },
  methods: {
    getYoutubeThumbnail,
    addMultimedia(event) {
      this.multimedia_form = [...this.multimedia_form, ...event]
      this.$modal.hide('modal_companyMultimediaAddForm')
    },
    updateMultimediaOrder(currentItemPosition, positionStep) {
      //   console.log(this.multimedia_form[key])
      const newItemPosition = currentItemPosition + positionStep

      if (newItemPosition <= this.multimedia_form.length - 1 || newItemPosition > 0) {
        this.multimedia_form.splice(newItemPosition, 0, {
          ...this.multimedia_form.splice(currentItemPosition, 1)[0],
        })
      }
    },
    deleteMultimedia(key) {
      this.$swal
        .fire({
          title: '¡Advertencia!',
          text:
            'Esta acción eliminará el recurso permanentemente.\n¿Estás seguro que quieres hacerlo?.',
          showConfirmButton: true,
          showCancelButton: true,
        })
        .then(result => {
          if (result.isConfirmed) {
            const multimediaToDelete = this.multimedia_form[key]

            if (multimediaToDelete._action != 'create') {
              const requestOptions = {
                preserveScroll: true,
                onBefore: () => {
                  this.$swal.fire({
                    title: 'Eliminando recurso',
                    text: 'Espera un momento...',
                    showConfirmButton: false,
                    showCancelButton: false,
                  })
                },
                onSuccess: () => {
                  this.$swal.fire({
                    title: 'Recurso eliminado con éxito',
                    showConfirmButton: true,
                  })
                },
              }
              this.$inertia.delete(
                this.$route('company.multimedia.destroy', multimediaToDelete['id']),
                requestOptions,
              )
            }

            this.multimedia_form.splice(key, 1)
          }
        })
    },
    submitForm() {
      // Vamos a enviar el request por partes...
      this.$swal.fire({
        title: 'Guardando cambios...',
        text: 'Espera un momento...',
      })

      // 1. Actualizar la galeria, si hay cambios
      if (this.multimedia_form != this.previousData.multimedia) {
        const updateExistingItems = this.multimedia_form
          .filter(item => item._action != 'create')
          .map(([key, item]) => {
            return {
              ...item,
              order: key,
            }
          })
        const createItems = this.multimedia_form.filter(item => item._action == 'create')

        if (updateExistingItems.length > 0) {
          // update
        }
        if (createItems.length > 0) {
          this
        }
      }

      // 2. Actualizar el perfil de la empresa, si hay cambios
      if (this.form.isDirty) {
        this.form
          .transform(data => cleanNullValuesFromObject(data))
          .post(this.$route('company.profile.update'))
      }
    },
  },
}
</script>

<style></style>
